import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import { categoryQueryKeys } from '@/lib/services/categories/categories.query'
import { collectionQueryKeys } from '@/lib/services/collections/collections.query'
import { customerReviewQueryKeys } from '@/lib/services/customer-reviews/customer-reviews.query'
import { customerQueryKeys } from '@/lib/services/customers/customers.query'
import { invalidateDesignQueries } from '@/lib/services/designs/designs.query'
import { inventoryQueryKeys } from '@/lib/services/inventory/inventory.keys'
import { locationQueryKeys } from '@/lib/services/locations/locations.query'
import { notificationRecipientQueryKeys } from '@/lib/services/notification-recipients/notification-recipients.query'
import { productQueryKeys } from '@/lib/services/products/products.query'
import { useMutation, useQuery } from '@tanstack/react-query'
import { addOnSetQueryKeys } from '../add-on-sets/addOnSets.keys'
import {
  getActiveFsnImportAttempt,
  getFsnImportEstimate,
  getFsnImportAttempt,
  previewFsnImportPlan,
  searchFsnClients,
  startFsnImportAttempt,
  type FsnImportAttemptInput,
  type FsnImportPlanInput,
} from './fsn-imports.api'

export const fsnImportQueryKeys = {
  clientSearch: ({
    organizationId,
    query,
    limit,
  }: {
    organizationId: number
    query: string
    limit: number
  }) => [
    {
      scope: 'fsn-imports',
      entity: 'clients',
      organizationId,
      query,
      limit,
    },
  ],
  plan: ['fsn-imports', 'plan'],
  estimate: ({
    organizationId,
    fsnClientId,
    fsnUsername,
  }: {
    organizationId: number
    fsnClientId?: number | null
    fsnUsername?: string | null
  }) => ['fsn-imports', 'estimate', organizationId, fsnClientId, fsnUsername ?? null],
  startAttempt: ['fsn-imports', 'start-attempt'],
  activeAttempt: ({ organizationId }: { organizationId: number }) => [
    'fsn-imports',
    'active-attempt',
    organizationId,
  ],
  attempt: ({
    organizationId,
    importAttemptId,
  }: {
    organizationId: number
    importAttemptId?: string | null
  }) => ['fsn-imports', 'attempt', organizationId, importAttemptId],
}

export function invalidateFsnImportTouchedQueries({ organizationId }: { organizationId: number }) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: productQueryKeys.all({ organizationId }) }),
    queryClient.invalidateQueries({ queryKey: collectionQueryKeys.all({ organizationId }) }),
    queryClient.invalidateQueries({ queryKey: categoryQueryKeys.all({ organizationId }) }),
    queryClient.invalidateQueries({ queryKey: inventoryQueryKeys.all({ organizationId }) }),
    queryClient.invalidateQueries({
      queryKey: notificationRecipientQueryKeys.all({ organizationId }),
    }),
    queryClient.invalidateQueries({ queryKey: customerQueryKeys.all({ organizationId }) }),
    queryClient.invalidateQueries({ queryKey: customerReviewQueryKeys.all }),
    queryClient.invalidateQueries({ queryKey: locationQueryKeys.all({ organizationId }) }),
    queryClient.invalidateQueries({ queryKey: addOnSetQueryKeys.all({ organizationId }) }),
    invalidateDesignQueries({ organizationId }),
  ])
}

export function useFsnImportEstimateQuery({
  fsnClientId,
  fsnUsername,
  enabled = true,
}: {
  /** The query waits until a client is chosen. */
  fsnClientId?: number | null
  fsnUsername?: string | null
  enabled?: boolean
}) {
  const organizationId = useRouteOrganizationId()

  return useQuery({
    queryKey: fsnImportQueryKeys.estimate({ organizationId, fsnClientId, fsnUsername }),
    enabled: Boolean(enabled && fsnClientId),
    // The fetcher needs the client; without one the query stays disabled.
    queryFn: fsnClientId
      ? () =>
          getFsnImportEstimate({
            organizationId,
            fsnClientId,
            fsnUsername,
          })
      : undefined,
    retry: false,
    staleTime: 5 * 60_000,
  })
}

export function isFsnClientSearchReady(query: string | null | undefined) {
  const normalizedQuery = String(query ?? '').trim()

  return normalizedQuery.length >= 3 || /^\d{2,10}$/.test(normalizedQuery)
}

export function useFsnClientSearchQuery({
  query,
  enabled = true,
  limit = 10,
}: {
  query: string | null | undefined
  enabled?: boolean
  limit?: number
}) {
  const organizationId = useRouteOrganizationId()
  const normalizedQuery = String(query ?? '').trim()

  return useQuery({
    queryKey: fsnImportQueryKeys.clientSearch({
      organizationId,
      query: normalizedQuery,
      limit,
    }),
    enabled: Boolean(enabled && isFsnClientSearchReady(normalizedQuery)),
    queryFn: () => searchFsnClients({ organizationId, query: normalizedQuery, limit }),
    retry: false,
    staleTime: 30_000,
  })
}

export function usePreviewFsnImportPlanMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationKey: fsnImportQueryKeys.plan,
    retry: false,
    mutationFn: ({
      fsnClientId,
      fsnUsername,
      sections,
      productOptions,
      websiteNavigation,
    }: FsnImportPlanInput) =>
      previewFsnImportPlan({
        organizationId,
        fsnClientId,
        fsnUsername,
        sections,
        productOptions,
        websiteNavigation,
      }),
  })
}

export function useStartFsnImportAttemptMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationKey: fsnImportQueryKeys.startAttempt,
    retry: false,
    mutationFn: ({
      fsnClientId,
      fsnUsername,
      sections,
      productOptions,
      websiteNavigation,
      planFingerprint,
      importAttemptId,
    }: FsnImportAttemptInput) =>
      startFsnImportAttempt({
        organizationId,
        fsnClientId,
        fsnUsername,
        sections,
        productOptions,
        websiteNavigation,
        planFingerprint,
        importAttemptId,
      }),
  })
}

export function useActiveFsnImportAttemptQuery({ enabled = true }: { enabled?: boolean } = {}) {
  const organizationId = useRouteOrganizationId()

  return useQuery({
    queryKey: fsnImportQueryKeys.activeAttempt({ organizationId }),
    enabled: Boolean(enabled),
    queryFn: () => getActiveFsnImportAttempt({ organizationId }),
    refetchOnMount: 'always',
    retry: false,
  })
}

export function useFsnImportAttemptQuery(
  /** The query waits until an attempt is started or recovered. */
  importAttemptId: string | null | undefined,
  { enabled = true }: { enabled?: boolean } = {}
) {
  const organizationId = useRouteOrganizationId()

  return useQuery({
    queryKey: fsnImportQueryKeys.attempt({ organizationId, importAttemptId }),
    enabled: Boolean(enabled && importAttemptId),
    // The fetcher needs the attempt id; without one the query stays disabled.
    queryFn: importAttemptId
      ? () => getFsnImportAttempt({ organizationId, importAttemptId })
      : undefined,
    refetchInterval: (query) => {
      const status = query.state.data?.status

      return status === 'queued' || status === 'running' ? 2000 : false
    },
  })
}
